/**
 * Bookings: the board and the list, as ONE surface with a toggle.
 *
 * ┌─ ⛔ AMIR 2026-09-15, AND THE WIREFRAME HAD ALREADY DRAWN IT ────────────────┐
 * │ `wireframes-master.html` carries a tab strip reading **Board | List |       │
 * │ Automations** with the pipeline selector beside it, drawn on BOTH views.    │
 * │                                                                            │
 * │ ⛔ THE BOARD SHIPPED ALONE BECAUSE THE SCOPE DOCUMENT NAMES THEM IN SEPARATE │
 * │ ITEMS (27 and 28) AND NEITHER SAYS WHERE IT LIVES. Item 7's shape: accurate  │
 * │ by omission. The entry is corrected; this file is the correction built.     │
 * │                                                                            │
 * │ ⚠️ AND THE STRIP WAS THREE LINES ABOVE THE COLUMN HEADERS TAKEN OFF THE SAME │
 * │ FILE for the board. The verb reading found it - late, which is the honest    │
 * │ half of saying the reading reduces the class rather than closing it.        │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ THE TAB IS IN THE URL, like the view and the pipeline, so a person can send
 * a colleague the LIST of the Snorkel pipeline's lost bookings. And the other
 * parameters survive the toggle: switching view keeps your place.
 *
 * ⚠️ AUTOMATIONS IS THE STRIP'S THIRD TAB AND IT IS NOT THIS PHASE'S. It is drawn
 * DISABLED with a reason rather than omitted, because a tab that exists in the
 * design and nowhere in the product is exactly what the wireframe reading is for.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchBoard, fetchBookingList, type Board, type BookingListPage } from '../data/bookings-api.js'
import { BookingBoard } from './BookingBoard.js'
import { BookingList } from './BookingList.js'
import { PipelineEditor } from './PipelineEditor.js'
import { FieldAdmin } from './FieldAdmin.js'
import './board.css'

const VIEWS = [
  { key: 'active', label: 'Active' },
  { key: 'mine', label: 'Mine' },
  { key: 'no-task', label: 'No Task' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
] as const

const COUNT = new Intl.NumberFormat()

type Props = {
  params: URLSearchParams
  onParamsChange: (next: URLSearchParams) => void
  onOpenBooking: (reference: number) => void
  onOpenContact: (reference: number) => void
}

export function BookingsSection({ params, onParamsChange, onOpenBooking, onOpenContact }: Props) {
  const tab = params.get('tab') === 'list' ? 'list' : 'board'
  const view = params.get('view') ?? 'active'
  const pipeline = params.get('pipeline') ?? ''
  const [board, setBoard] = useState<Board | null>(null)
  const [list, setList] = useState<BookingListPage | null>(null)
  const [failed, setFailed] = useState(false)
  /** ⚠️ Bumped after any write, so both views refetch through one dependency
   *  rather than each holding its own reload flag. */
  const [revision, setRevision] = useState(0)
  /** ⚠️ Which question the data on screen answers, so a refetch can tell "the
   *  same board again" from "a different board". */
  const lastQuestion = useRef('')
  const [editingPipelines, setEditingPipelines] = useState(false)
  const [editingFields, setEditingFields] = useState(false)

  const sort = params.get('sort') ?? ''
  const direction = params.get('direction') ?? ''
  const q = params.get('q') ?? ''
  const page = params.get('page') ?? ''
  const limit = params.get('limit') ?? ''

  /**
   * ┌─ ⛔ A REFETCH DOES NOT BLANK THE BOARD, AND A BROWSER TEST FOUND OUT WHY ─┐
   * │ The first version set the data to `null` before every fetch, which made   │
   * │ the whole board unmount - **including the live region holding "moved to   │
   * │ Quoted"**. So every successful move wiped the sentence a screen reader was │
   * │ about to read, and replaced it with a skeleton flash for everybody else.  │
   * │                                                                          │
   * │ ⚠️ AND IT WAS FLAKY RATHER THAN BROKEN, which is worse: the assertion won  │
   * │ the race on a fresh corpus and lost it on the second run. A move that      │
   * │ announces nothing is not something an inspection would reliably catch.    │
   * └──────────────────────────────────────────────────────────────────────────┘
   *
   * ⛔ SO STALE DATA STAYS ON SCREEN WHILE THE NEW DATA IS FETCHED, and the
   * skeleton shows only when there is nothing to show at all.
   */
  useEffect(() => {
    let current = true
    setFailed(false)
    /**
     * ⛔ BLANK ONLY WHEN THE QUESTION CHANGED, NOT WHEN THE ANSWER MIGHT HAVE.
     * A different pipeline or view is a different board and showing the old one
     * would leave the selector disagreeing with the columns under it. A refetch
     * after a move is the SAME board, so the stale copy is correct until the new
     * one lands - and the live region survives.
     */
    /**
     * ⚠️ `JSON.stringify` RATHER THAN A JOIN, AND THE FIRST VERSION IS WHY. It
     * joined on a unit separator, which lands in the file as a RAW CONTROL BYTE
     * (`seven-control-bytes-in-one-phase-and-four-were-in-the-checker`):
     * invisible in a diff and unsearchable by grep. ⛔ **Third instance of that
     * class today**, after the NUL sentinel in `App.tsx` and the comment about it
     * that contained one - and this time the Bash tool refused to let me type the
     * character at all, which is the guard arriving from a third direction.
     *
     * ⚠️ IT IS ALSO THE CORRECT ENCODING: it cannot collide whatever a query
     * parameter contains, where any single separator can.
     */
    const question = JSON.stringify([tab, view, pipeline, sort, direction, q, page, limit])
    if (lastQuestion.current !== question) {
      lastQuestion.current = question
      setBoard(null)
      setList(null)
    }
    if (tab === 'board') {
      void fetchBoard({ view, pipeline })
        .then((b) => current && setBoard(b))
        .catch(() => current && setFailed(true))
    } else {
      void fetchBookingList({
        view,
        pipeline,
        sort,
        direction,
        q,
        page: Number(page) || 1,
        ...(limit === '' ? {} : { limit: Number(limit) }),
      })
        .then((p) => current && setList(p))
        .catch(() => current && setFailed(true))
    }
    return () => {
      current = false
    }
  }, [tab, view, pipeline, sort, direction, q, page, limit, revision])

  /**
   * ⛔ IT TAKES A RECORD, NOT A KEY AND A VALUE, AND A BROWSER TEST IS WHY. The
   * first version was `setParam(key, value)`, and the list's column header calls
   * it twice - once for `sort`, once for `direction`. **Both read the same
   * `params` prop**, so the second built its copy from the ORIGINAL and the
   * first change was silently discarded. Sorting did nothing and nothing errored.
   *
   * ⚠️ THE SHAPE IS THE FIX, NOT A FUNCTIONAL SETTER. `onParamsChange` navigates;
   * there is no state to read a previous value from, so the only way two changes
   * survive is to make them one change.
   */
  const setParams = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(params)
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key)
        else next.set(key, value)
      }
      /** ⛔ ANY CHANGE OF FILTER RETURNS TO PAGE ONE. Otherwise narrowing 800
       *  bookings to four while on page 6 shows an empty list that looks like
       *  "no matches" - the contacts list's own finding, honoured here first. */
      if (!('page' in changes)) next.delete('page')
      onParamsChange(next)
    },
    [params, onParamsChange],
  )

  const setParam = useCallback(
    (key: string, value: string | null) => setParams({ [key]: value }),
    [setParams],
  )

  const onChanged = useCallback(() => setRevision((r) => r + 1), [])

  const chrome = tab === 'board' ? board : (list?.chrome ?? null)
  const pipelines = tab === 'board' ? (board?.pipelines ?? []) : (list?.chrome?.pipelines ?? [])
  const pipelineId = tab === 'board' ? (board?.pipelineId ?? '') : (list?.chrome?.pipelineId ?? '')
  const viewCounts = tab === 'board' ? board?.viewCounts : list?.chrome?.viewCounts

  if (failed) {
    return (
      <main className="board" data-testid="board-error">
        <p className="board__state" role="alert">
          Bookings could not be loaded.
        </p>
      </main>
    )
  }

  return (
    <main className="board" data-testid="board" data-tab={tab} data-pipeline={pipelineId}>
      <header className="board__bar">
        {/**
         * ⛔ BOARD | LIST | AUTOMATIONS, the wireframe's own strip. A tablist
         * rather than links, because the three show the same bookings and only
         * the presentation changes - which is what a tab means.
         */}
        <div className="board__tabs" role="tablist" aria-label="Bookings View">
          <button
            type="button"
            role="tab"
            className={tab === 'board' ? 'board__tab board__tab--on' : 'board__tab'}
            aria-selected={tab === 'board'}
            data-testid="bookings-tab-board"
            onClick={() => setParam('tab', null)}
          >
            Board
          </button>
          <button
            type="button"
            role="tab"
            className={tab === 'list' ? 'board__tab board__tab--on' : 'board__tab'}
            aria-selected={tab === 'list'}
            data-testid="bookings-tab-list"
            onClick={() => setParam('tab', 'list')}
          >
            List
          </button>
          {/**
           * ⛔ DISABLED WITH A REASON RATHER THAN OMITTED. Automations is a
           * later phase, and a tab silently missing from a strip the design
           * draws is how a gap becomes invisible. The title says which phase.
           *
           * ⚠️ AND **Edit Stages** GOT THE OPPOSITE CALL, which is deliberate:
           * the wireframe draws it beside the selector and nothing can create or
           * reorder a stage, but its absence is obvious the moment somebody wants
           * it, where a missing TAB would hide a whole section.
           * `edit-stages-has-no-owner-and-the-strip-names-it`.
           */}
          <button
            type="button"
            role="tab"
            className="board__tab board__tab--off"
            aria-selected={false}
            disabled
            data-testid="bookings-tab-automations"
            title="Automations are a later phase and are not built yet."
          >
            Automations
          </button>
        </div>

        {/**
         * ⛔ THE PIPELINE SELECTOR. ⚠️ Switching ships UNGATED with that stated:
         * `Build_Order` wants a permission gate and the permission model is
         * ADMIN-ANALYTICS's and does not exist. A control that pretends to check
         * is worse than one that says it does not.
         */}
        <label className="board__pipeline-label" htmlFor="board-pipeline">
          Pipeline
        </label>
        <select
          id="board-pipeline"
          className="board__pipeline"
          data-testid="board-pipeline"
          value={pipelineId}
          disabled={pipelines.length === 0}
          onChange={(e) => setParam('pipeline', e.target.value)}
        >
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>

        {/**
         * ⛔ **Edit Stages**, WHERE THE WIREFRAME PUTS IT: beside the selector.
         * Built 2026-09-15 on Amir's ruling. ⚠️ Until then `pipelines` and
         * `pipeline_stages` had ZERO writers outside the seeder, so a deployment
         * that had not run it could hold no bookings at all - `bookings.
         * pipeline_id` is NOT NULL. **Unusable rather than incomplete**, which is
         * what made this different from `partners`' inertness.
         */}
        <button
          type="button"
          className="board__configure"
          data-testid="board-edit-stages"
          onClick={() => setEditingPipelines(true)}
        >
          Edit Stages
        </button>

        {/**
         * ⛔ CUSTOM FIELDS, REACHED FROM THE SURFACE THEY CONFIGURE. Item 3 calls
         * these tables the root of the phase and **the seeder was their only
         * writer** - so every custom field in the product came from a script.
         *
         * ⚠️ IT IS HERE RATHER THAN IN A SETTINGS SECTION because there is no
         * settings section: §13's nav has seven items and two are built. Putting
         * it beside the thing it configures is the same call `Configure Cards`
         * took, and it moves when ADMIN-ANALYTICS builds somewhere for it.
         */}
        <button
          type="button"
          className="board__configure"
          data-testid="board-custom-fields"
          onClick={() => setEditingFields(true)}
        >
          Custom Fields
        </button>

        {/** ⛔ EVERY CHIP CARRIES ITS COUNT, so its effect is known before it is
         *  clicked. ⚠️ The counts come from ONE server-side function whichever
         *  tab is showing, so the same chip cannot read 12 here and 11 there. */}
        <div className="board__views">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={view === v.key ? 'board__view board__view--on' : 'board__view'}
              aria-pressed={view === v.key}
              data-testid={`board-view-${v.key}`}
              onClick={() => setParam('view', v.key === 'active' ? null : v.key)}
            >
              {v.label}{' '}
              <span className="board__view-count">
                {viewCounts === undefined ? '' : COUNT.format(viewCounts[v.key])}
              </span>
            </button>
          ))}
        </div>
      </header>

      {chrome === null && board === null && list === null ? (
        <div className="board__columns" data-testid="board-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="board__column">
              <div className="record__skeleton record__skeleton--title" />
              <div className="record__skeleton" />
              <div className="record__skeleton" />
            </div>
          ))}
        </div>
      ) : tab === 'board' ? (
        <BookingBoard
          board={board}
          onOpenBooking={onOpenBooking}
          onChanged={onChanged}
          onSaved={onChanged}
        />
      ) : (
        <BookingList
          page={list}
          params={params}
          setParam={setParam}
          setParams={setParams}
          onOpenBooking={onOpenBooking}
          onOpenContact={onOpenContact}
          onChanged={onChanged}
        />
      )}

      {/** ⚠️ `onSaved` BUMPS THE REVISION AND KEEPS THE DIALOG OPEN, because
       *  editing stages is usually several edits. Closing on each save would
       *  make renaming three stages three round trips through a button. */}
      {editingPipelines && (
        <PipelineEditor onClose={() => setEditingPipelines(false)} onSaved={onChanged} />
      )}

      {/** ⚠️ `onChanged` ON CLOSE RATHER THAN ON EACH SAVE: adding a date field
       *  changes what a card can show, so the board refetches once the person is
       *  done rather than after every step of the configuration. */}
      {editingFields && (
        <FieldAdmin
          owner="booking"
          onClose={() => {
            setEditingFields(false)
            onChanged()
          }}
        />
      )}
    </main>
  )
}
